import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useBootstrapSelector } from '../../redux/selectorHooks';
import { assertNotNil } from '../../utils/types';
import { useAppDispatch } from '../../redux/hooks';
import { ITooltipOption } from '../KubitTooltip/globalTooltips';
import BuilderEnumSelect from '../Builder/BuilderFields/BuilderEnumSelect';
import { globalFormat } from '../../utils/formatting';
import { IStyled } from '../../styles/types';
import { setActiveSchemaId } from '../../redux/slices/userSettings';
import useSyndicateTimezoneLabel from '../../utils/hooks/useSyndicateTimezoneLabel';

const MAX_VISIBLE_OPTIONS = 8;
const OPTION_HEIGHT = 28;

const HintText = styled.div`
  font-size: 12px;
  font-weight: 500;
  line-height: 1.5;
`;

interface IProps extends IStyled {
  value: string;
  onChange: (newSchemaId: string) => void;
  permittedSchemaIds?: string[];
}

const SchemaDropdownMenu = ({
  value,
  onChange,
  className,
  permittedSchemaIds,
}: IProps) => {
  const dispatch = useAppDispatch();
  const { dateFormat, timeFormat } = globalFormat.getGlobalFormat();
  const schemaConfig = useBootstrapSelector('schemaConfig');
  const shortSyndicateTimeZone = useSyndicateTimezoneLabel();
  assertNotNil(schemaConfig);
  const { schemas } = schemaConfig;
  const permittedSchemas =
    permittedSchemaIds && permittedSchemaIds.length > 0
      ? schemas.filter((schemaId) => permittedSchemaIds.includes(schemaId.id))
      : schemas;

  const schemaObj = permittedSchemas.find((s) => s.id === value);
  const latestDataAt = schemaObj?.latestDataAt;

  const tooltipOption: ITooltipOption | undefined = useMemo(() => {
    // if there is no data refresh info - do not display a tooltip
    if (!latestDataAt) {
      return;
    }

    // otherwise display a tooltip with data refresh timestamps
    const maxDataTsStr = globalFormat.formatDate(latestDataAt, {
      dateFormat,
      timeFormat,
    });

    return {
      content: (
        <>
          <HintText>Select different data schemas.</HintText>
          {maxDataTsStr && (
            <HintText>
              Latest data: {maxDataTsStr} <b>{shortSyndicateTimeZone}</b>
            </HintText>
          )}
        </>
      ),
    };
  }, [timeFormat, dateFormat, latestDataAt, shortSyndicateTimeZone]);

  const handleChange = (newSchemaId: string) => {
    dispatch(setActiveSchemaId(newSchemaId));
    onChange(newSchemaId);
  };

  const enumOptions = useMemo(
    () =>
      permittedSchemas.map(({ name, id, description }) => ({
        name,
        key: id,
        tooltip: description ? { content: description } : undefined,
      })),
    [permittedSchemas],
  );

  const maxMenuHeight = useMemo(() => {
    const maxVisibleOptions = Math.min(
      MAX_VISIBLE_OPTIONS,
      permittedSchemas.length,
    );
    // XXX display half of the first overflowing schema name - to make it more obvious that there are more
    return maxVisibleOptions * OPTION_HEIGHT + OPTION_HEIGHT / 2;
  }, [permittedSchemas.length]);

  return (
    <BuilderEnumSelect
      label={'Schema'}
      enumOptions={enumOptions}
      onChange={handleChange}
      selectedOption={value}
      className={className}
      tooltipOption={tooltipOption}
      maxMenuHeight={maxMenuHeight}
    />
  );
};

export default SchemaDropdownMenu;
